import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  changeMemberRole,
  createOrganisation,
  inviteMember,
  listMembers,
  listTenantInvitations,
  removeMember,
  revokeInvitation,
  updateTenantSettings,
  type ClaimVisibility,
  type Tenant,
  type TenantInvitation,
  type TenantMember,
  type TenantRole
} from '@peruselab/api-client/tenants';
import { Icon } from '../components/Icon';
import { useAuth } from '../auth/useAuth';
import { useActiveTenant } from './activeTenant';

const ROLES: readonly TenantRole[] = ['VIEWER', 'EDITOR', 'ADMINISTRATOR'];

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * #1051 -- the signed-in user's organisations (P1, P2): create one, or a sub-organisation of one
 * they administer; see the members; and, as an administrator, change roles, invite by email,
 * revoke pending invitations and set the organisation's settings (ADR 0019 D4, D7). A
 * sub-organisation is listed under its parent when the user belongs to both, but its membership
 * is its own: nothing is inherited.
 */
export function OrganisationsPage() {
  const { t } = useTranslation();
  const { tenants, refresh } = useActiveTenant();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();
  const parentFieldId = useId();
  const parents = tenants.filter((tenant) => tenant.type === 'ORG' && tenant.role === 'ADMINISTRATOR');
  // Parents first, each followed by the sub-organisations the user belongs to.
  const ordered = [
    ...tenants
      .filter((tenant) => tenant.type === 'ORG')
      .flatMap((parent) => [parent, ...tenants.filter((tenant) => tenant.parentId === parent.id)]),
    ...tenants.filter((tenant) => tenant.type === 'SUB_ORG' && !tenants.some((other) => other.id === tenant.parentId))
  ];

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createOrganisation(name, parentId || undefined);
      setName('');
      setParentId('');
      await refresh();
    } catch (caught) {
      setError(messageOf(caught, t('tenancy.page.createFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="organisations-page">
      <h1>{t('tenancy.page.heading')}</h1>
      <p className="organisations-intro">{t('tenancy.page.intro')}</p>

      <form className="organisation-create" onSubmit={(event) => void create(event)}>
        <label htmlFor={nameId}>{t('tenancy.page.newName')}</label>
        <input
          id={nameId}
          value={name}
          maxLength={100}
          onChange={(event) => setName(event.target.value)}
          dir="auto"
          required
        />
        {parents.length > 0 ? (
          <>
            <label htmlFor={parentFieldId}>{t('tenancy.page.parent')}</label>
            <select id={parentFieldId} value={parentId} onChange={(event) => setParentId(event.target.value)}>
              <option value="">{t('tenancy.page.noParent')}</option>
              {parents.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.name}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <button
          type="submit"
          className="icon-button"
          disabled={busy || name.trim() === ''}
          aria-label={t('tenancy.page.create')}
          title={t('tenancy.page.create')}
        >
          <Icon name="add" />
        </button>
        {error ? (
          <p className="import-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>

      {ordered.length === 0 ? <p>{t('tenancy.page.none')}</p> : null}
      <ul className="organisation-list">
        {ordered.map((tenant) => (
          <li key={tenant.id}>
            <OrganisationCard
              tenant={tenant}
              parentName={tenants.find((other) => other.id === tenant.parentId)?.name}
              onChanged={refresh}
            />
          </li>
        ))}
      </ul>
    </main>
  );
}

function OrganisationCard({
  tenant,
  parentName,
  onChanged
}: {
  readonly tenant: Tenant;
  readonly parentName: string | undefined;
  readonly onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { active, switchTo } = useActiveTenant();
  const me = user?.profile.sub ?? '';
  const administrator = tenant.role === 'ADMINISTRATOR';
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState<readonly TenantMember[] | null>(null);
  const [invitations, setInvitations] = useState<readonly TenantInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [loadedMembers, pending] = await Promise.all([
        listMembers(tenant.id),
        administrator ? listTenantInvitations(tenant.id) : Promise.resolve([] as readonly TenantInvitation[])
      ]);
      setMembers(loadedMembers);
      setInvitations(pending);
    } catch (caught) {
      setError(messageOf(caught, t('tenancy.page.loadFailed')));
    }
  }, [administrator, t, tenant.id]);

  useEffect(() => {
    // Loading the members when the card is opened is this effect's job.
    // eslint-disable-next-line react/set-state-in-effect
    if (open && members === null) void load();
  }, [load, members, open]);

  const act = async (work: () => Promise<unknown>, done?: string) => {
    setError(null);
    setNotice(null);
    try {
      await work();
      if (done) setNotice(done);
      await load();
    } catch (caught) {
      setError(messageOf(caught, t('tenancy.page.actionFailed')));
    }
  };

  const leave = async () => {
    if (!globalThis.confirm(t('tenancy.page.leaveConfirm', { name: tenant.name }))) return;
    setError(null);
    try {
      await removeMember(tenant.id, me);
      if (active?.id === tenant.id) switchTo(null);
      await onChanged();
    } catch (caught) {
      setError(messageOf(caught, t('tenancy.page.actionFailed')));
    }
  };

  return (
    <section className="organisation-card" data-testid="organisation-card" aria-label={tenant.name}>
      <header className="organisation-card-header">
        <Icon name="corporateFare" />
        <div className="organisation-card-title">
          <h2 dir="auto">{tenant.name}</h2>
          <p className="organisation-card-meta">
            {tenant.type === 'SUB_ORG'
              ? t('tenancy.page.subOf', { parent: parentName ?? t('tenancy.page.parentHidden') })
              : t('tenancy.page.organisation')}
            {' · '}
            {t(`tenancy.roles.${tenant.role}`)}
          </p>
        </div>
        {active?.id === tenant.id ? (
          <span className="organisation-active-badge">{t('tenancy.page.acting')}</span>
        ) : (
          <button type="button" onClick={() => switchTo(tenant.id)}>
            {t('tenancy.page.actFor')}
          </button>
        )}
      </header>

      <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
        <summary>
          <Icon name="group" size="1.1rem" />
          <span>{t('tenancy.page.members')}</span>
        </summary>
        {members === null ? <p role="status">{t('common.loading')}</p> : null}
        {members ? (
          <ul className="organisation-members">
            {members.map((member) => (
              <li key={member.userId} data-testid="organisation-member">
                <span dir="auto">
                  {member.displayName}
                  {member.userId === me ? ` ${t('tenancy.page.you')}` : ''}
                </span>
                {administrator ? (
                  <select
                    aria-label={t('tenancy.page.roleOf', { name: member.displayName })}
                    value={member.role}
                    onChange={(event) =>
                      void act(() => changeMemberRole(tenant.id, member.userId, event.target.value as TenantRole))
                    }
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {t(`tenancy.roles.${role}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="organisation-member-role">{t(`tenancy.roles.${member.role}`)}</span>
                )}
                {administrator && member.userId !== me ? (
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={t('tenancy.page.remove', { name: member.displayName })}
                    title={t('tenancy.page.remove', { name: member.displayName })}
                    onClick={() => void act(() => removeMember(tenant.id, member.userId))}
                  >
                    <Icon name="close" size="1.1rem" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {administrator && members ? (
          <>
            <InviteForm
              tenant={tenant}
              onInvite={(email, role) =>
                act(() => inviteMember(tenant.id, email, role), t('tenancy.page.invited', { email }))
              }
            />
            {invitations.length > 0 ? (
              <ul className="organisation-invitations" aria-label={t('tenancy.page.pending')}>
                {invitations.map((invitation) => (
                  <li key={invitation.id} data-testid="organisation-invitation">
                    <Icon name="mail" size="1rem" />
                    <span>{invitation.email}</span>
                    <span className="organisation-member-role">{t(`tenancy.roles.${invitation.role}`)}</span>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={t('tenancy.page.revoke', { email: invitation.email })}
                      title={t('tenancy.page.revoke', { email: invitation.email })}
                      onClick={() => void act(() => revokeInvitation(tenant.id, invitation.id))}
                    >
                      <Icon name="close" size="1.1rem" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <SettingsForm
              tenant={tenant}
              onSave={(claimVisibility, outsideLookups) =>
                act(async () => {
                  await updateTenantSettings(tenant.id, claimVisibility, outsideLookups);
                  await onChanged();
                }, t('tenancy.page.settingsSaved'))
              }
            />
          </>
        ) : null}
      </details>

      {notice ? (
        <p className="organisation-notice" role="status">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        className="organisation-leave icon-button danger"
        onClick={() => void leave()}
        aria-label={t('tenancy.page.leave')}
        title={t('tenancy.page.leave')}
      >
        <Icon name="logout" />
      </button>
    </section>
  );
}

function InviteForm({
  tenant,
  onInvite
}: {
  readonly tenant: Tenant;
  readonly onInvite: (email: string, role: TenantRole) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('EDITOR');
  const emailId = useId();
  const roleId = useId();
  return (
    <form
      className="organisation-invite"
      aria-label={t('tenancy.page.invite', { name: tenant.name })}
      onSubmit={(event) => {
        event.preventDefault();
        void onInvite(email.trim(), role).then(() => setEmail(''));
      }}
    >
      <label htmlFor={emailId}>{t('tenancy.page.inviteEmail')}</label>
      <input id={emailId} type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
      <label htmlFor={roleId}>{t('tenancy.page.inviteRole')}</label>
      <select id={roleId} value={role} onChange={(event) => setRole(event.target.value as TenantRole)}>
        {ROLES.map((option) => (
          <option key={option} value={option}>
            {t(`tenancy.roles.${option}`)}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className="icon-button"
        disabled={email.trim() === ''}
        aria-label={t('tenancy.page.sendInvite')}
        title={t('tenancy.page.sendInvite')}
      >
        <Icon name="mail" />
      </button>
    </form>
  );
}

function SettingsForm({
  tenant,
  onSave
}: {
  readonly tenant: Tenant;
  readonly onSave: (claimVisibility: ClaimVisibility, outsideLookups: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [claimVisibility, setClaimVisibility] = useState<ClaimVisibility>(tenant.claimVisibility);
  const [outsideLookups, setOutsideLookups] = useState(tenant.outsideLookups);
  const changed = claimVisibility !== tenant.claimVisibility || outsideLookups !== tenant.outsideLookups;
  return (
    <form
      className="organisation-settings"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(claimVisibility, outsideLookups);
      }}
    >
      <fieldset>
        <legend>
          <Icon name="settings" size="1rem" />
          <span>{t('tenancy.settings.claimVisibility')}</span>
        </legend>
        {(['ORGANISATION', 'CREATOR'] as const).map((option) => (
          <label key={option}>
            <input
              type="radio"
              name={`claim-visibility-${tenant.id}`}
              checked={claimVisibility === option}
              onChange={() => setClaimVisibility(option)}
            />
            {t(`tenancy.settings.claimVisibilityOptions.${option}`)}
          </label>
        ))}
      </fieldset>
      <label>
        <input type="checkbox" checked={outsideLookups} onChange={(event) => setOutsideLookups(event.target.checked)} />
        {t('tenancy.settings.outsideLookups')}
      </label>
      <button
        type="submit"
        className="icon-button"
        disabled={!changed}
        aria-label={t('tenancy.settings.save')}
        title={t('tenancy.settings.save')}
      >
        <Icon name="save" />
      </button>
    </form>
  );
}
